<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
  Boxes,
  ChevronDown,
  Code2,
  Menu,
  Search,
  Smartphone,
  Video,
  X,
} from 'lucide-vue-next'
import BrandMark from './BrandMark.vue'
import { NOVA_AI_URL } from '@/config/external-links'
import { getVideoHomeCopy, type HomeLocale } from '@/data/videoHomeCopy'
import { authSession } from '@/services/auth'

const props = withDefaults(defineProps<{ locale?: HomeLocale }>(), { locale: 'zh' })
const menuOpen = ref(false)
const scrolled = ref(false)
const copy = computed(() => getVideoHomeCopy(props.locale).header)
const english = computed(() => props.locale === 'en')
const homePath = computed(() => props.locale === 'en' ? '/en' : '/')
const languagePath = computed(() => props.locale === 'en' ? '/' : '/en')
const discoverPath = computed(() => props.locale === 'en' ? { path: '/en', hash: '#video-cases' } : '/works')
const creatorsPath = computed(() => ({ path: homePath.value, hash: '#creators' }))
const tasksPath = computed(() => props.locale === 'en' ? { path: '/en', hash: '#business' } : '/tasks')
const coursesPath = computed(() => props.locale === 'en' ? { path: '/en', hash: '#video-courses' } : '/courses')
const businessPath = computed(() => ({ path: homePath.value, hash: '#business' }))
const accountPath = computed(() => authSession.isAuthenticated.value ? '/account' : '/login')
const accountLabel = computed(() => authSession.isAuthenticated.value ? (english.value ? 'Account' : '我的账号') : copy.value.login)
const updateScroll = () => { scrolled.value = window.scrollY > 32 }
const closeMenu = () => { menuOpen.value = false }
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') closeMenu()
}

watch(menuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

onMounted(() => {
  updateScroll()
  window.addEventListener('scroll', updateScroll, { passive: true })
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateScroll)
  window.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <header class="video-home-header" :class="{ scrolled, 'menu-open': menuOpen }">
    <div class="video-header-inner">
      <RouterLink class="video-brand" :to="homePath" :aria-label="copy.homeAria" @click="closeMenu">
        <BrandMark />
        <span><strong>Nova·云枢</strong><small>{{ copy.brandSubtitle }}</small></span>
      </RouterLink>

      <button class="video-mobile-menu" type="button" :aria-expanded="menuOpen" :aria-label="menuOpen ? copy.closeMenu : copy.openMenu" @click="menuOpen = !menuOpen">
        <X v-if="menuOpen" :size="22" aria-hidden="true" />
        <Menu v-else :size="22" aria-hidden="true" />
      </button>

      <nav class="video-main-nav" :class="{ open: menuOpen }" :aria-label="copy.navigationAria">
        <RouterLink :to="discoverPath" @click="closeMenu">{{ copy.discoverWorks }}</RouterLink>
        <RouterLink :to="creatorsPath" @click="closeMenu">{{ copy.creators }}</RouterLink>
        <RouterLink :to="tasksPath" @click="closeMenu">{{ copy.taskMarket }}</RouterLink>
        <RouterLink :to="coursesPath" @click="closeMenu">{{ copy.aigcClass }}</RouterLink>
        <RouterLink :to="businessPath" @click="closeMenu">{{ copy.businessCooperation }}</RouterLink>

        <div class="video-dropdown">
          <button type="button">{{ copy.productsAndServices }} <ChevronDown :size="14" aria-hidden="true" /></button>
          <div class="video-dropdown-panel product-panel">
            <a :href="NOVA_AI_URL" target="_blank" rel="noopener">
              <Video :size="20" aria-hidden="true" /><span><strong>{{ copy.aiStudio }}</strong><small>{{ copy.aiStudioStatus }}</small></span>
            </a>
            <RouterLink :to="businessPath" @click="closeMenu">
              <Boxes :size="20" aria-hidden="true" /><span><strong>{{ copy.yingliu }}</strong><small>{{ copy.yingliuStatus }}</small></span>
            </RouterLink>
            <RouterLink :to="businessPath" @click="closeMenu">
              <Smartphone :size="20" aria-hidden="true" /><span><strong>{{ copy.miniProgram }}</strong><small>{{ copy.miniProgramStatus }}</small></span>
            </RouterLink>
            <RouterLink :to="businessPath" @click="closeMenu">
              <Code2 :size="20" aria-hidden="true" /><span><strong>{{ copy.openPlatform }}</strong><small>{{ copy.openPlatformStatus }}</small></span>
            </RouterLink>
          </div>
        </div>

        <div class="video-mobile-actions">
          <RouterLink :to="accountPath" @click="closeMenu">{{ accountLabel }}</RouterLink>
          <RouterLink :to="businessPath" @click="closeMenu">{{ copy.openMiniProgram }}</RouterLink>
          <RouterLink :to="languagePath" :aria-label="copy.switchLanguageAria" @click="closeMenu">{{ copy.switchLanguage }}</RouterLink>
        </div>
      </nav>

      <div class="video-header-actions">
        <RouterLink class="search-link" :to="discoverPath" :aria-label="copy.searchWorks"><Search :size="18" aria-hidden="true" /></RouterLink>
        <RouterLink class="login-link" :to="accountPath">{{ accountLabel }}</RouterLink>
        <RouterLink class="language-link" :to="languagePath" :aria-label="copy.switchLanguageAria">{{ copy.switchLanguage }}</RouterLink>
        <RouterLink class="mini-program-link" :to="businessPath"><Smartphone :size="17" aria-hidden="true" />{{ copy.openMiniProgram }}</RouterLink>
      </div>
    </div>
  </header>
</template>

<style scoped>
.video-home-header {
  position: fixed;
  z-index: 80;
  inset: 0 0 auto;
  color: #fff;
  transition: color .28s ease, background .28s ease, backdrop-filter .28s ease;
}
.video-home-header.scrolled,
.video-home-header.menu-open {
  color: #0a0d11;
  background: rgba(247, 248, 246, .9);
  border-bottom: 1px solid rgba(10,13,17,.1);
  backdrop-filter: blur(22px);
}
.video-header-inner {
  width: min(1580px, calc(100% - 96px));
  height: 82px;
  margin: auto;
  display: grid;
  grid-template-columns: 260px 1fr auto;
  align-items: center;
  gap: 28px;
}
.video-brand { display: inline-flex; align-items: center; gap: 12px; width: max-content; }
.video-brand :deep(.brand-mark) img { height: 26px; }
.video-brand strong { display: block; font-size: 17px; }
.video-brand small { display: block; margin-top: 2px; opacity: .58; font-size: 10px; }
.video-main-nav { display: flex; align-items: center; justify-content: center; gap: clamp(20px, 1.8vw, 36px); font-size: 14px; }
.video-main-nav > a, .video-dropdown > button { opacity: .82; }
.video-main-nav > a:hover, .video-dropdown > button:hover { opacity: 1; }
.video-dropdown { position: relative; }
.video-dropdown > button { min-height: 76px; padding: 0; display: inline-flex; align-items: center; gap: 5px; color: inherit; border: 0; background: transparent; }
.video-dropdown-panel {
  position: absolute;
  top: 69px;
  left: 50%;
  width: 610px;
  padding: 11px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  color: #0a0d11;
  background: rgba(255,255,255,.97);
  border: 1px solid rgba(255,255,255,.85);
  border-radius: 18px;
  box-shadow: 0 32px 90px rgba(0,12,20,.23);
  backdrop-filter: blur(24px);
  opacity: 0;
  visibility: hidden;
  transform: translate(-50%, -10px);
  transition: opacity .18s ease, transform .18s ease, visibility .18s ease;
}
.video-dropdown:hover .video-dropdown-panel,
.video-dropdown:focus-within .video-dropdown-panel { opacity: 1; visibility: visible; transform: translate(-50%, 0); }
.video-dropdown-panel a { min-height: 82px; padding: 14px; display: grid; grid-template-columns: auto 1fr; align-items: start; gap: 12px; border-radius: 12px; }
.video-dropdown-panel a:hover { background: #edf8f9; }
.video-dropdown-panel svg { color: #0c8192; margin-top: 2px; }
.video-dropdown-panel strong { display: block; margin-bottom: 5px; font-size: 14px; }
.video-dropdown-panel small { color: #70777e; font-size: 11px; }
.video-header-actions { display: flex; align-items: center; gap: 14px; font-size: 14px; }
.search-link { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 50%; opacity: .82; }
.search-link:hover { background: rgba(255,255,255,.12); opacity: 1; }
.scrolled .search-link:hover { background: rgba(10,13,17,.07); }
.login-link { opacity: .82; }
.language-link { min-width: 30px; font-weight: 700; text-align: center; opacity: .82; }
.mini-program-link { min-height: 44px; padding: 0 18px; display: inline-flex; align-items: center; gap: 8px; color: #071b22; border-radius: 999px; background: #62dbea; font-weight: 700; }
.video-mobile-menu { display: none; }
.video-mobile-actions { display: none; }
@media (max-width: 1280px) {
  .video-header-inner { width: calc(100% - 30px); height: 68px; grid-template-columns: 1fr auto; }
  .video-brand small, .video-header-actions { display: none; }
  .video-mobile-menu { width: 42px; height: 42px; display: grid; place-items: center; color: inherit; border: 1px solid currentColor; border-radius: 50%; background: transparent; }
  .video-main-nav {
    position: fixed;
    top: 68px;
    left: 0;
    right: 0;
    max-height: calc(100svh - 68px);
    padding: 18px 15px 30px;
    display: none;
    overflow: auto;
    color: #0a0d11;
    background: rgba(247,248,246,.98);
  }
  .video-main-nav.open { display: grid; justify-content: stretch; gap: 4px; }
  .video-main-nav > a, .video-dropdown > button { width: 100%; min-height: 48px; display: flex !important; align-items: center; justify-content: space-between; padding: 0 12px; border-bottom: 1px solid rgba(10,13,17,.09); opacity: 1; }
  .video-dropdown-panel {
    position: static;
    width: 100%;
    padding: 4px 0 12px;
    grid-template-columns: 1fr;
    box-shadow: none;
    border: 0;
    border-radius: 0;
    background: transparent;
    opacity: 1;
    visibility: visible;
    transform: none;
  }
  .video-dropdown-panel a { min-height: 66px; background: #fff; }
  .video-mobile-actions { padding-top: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .video-mobile-actions a { min-height: 46px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; border: 1px solid rgba(10,13,17,.12); border-radius: 999px; background: #fff; font-weight: 700; }
  .video-mobile-actions a:nth-child(3) { color: #071b22; border-color: #62dbea; background: #62dbea; }
}
</style>
